import { ACTION_EVENT_QUERY_KEYS } from 'app/modules/actionEvents/queries/keys';
import { get } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectHasReadEventsPermission } from 'app/modules/session/selectors';
import {
  CachedActionEventResponse,
  ShortActionEventResponse,
} from 'app/modules/actionEvents/responses';
import { selectDLEnabledForActionEventDetail } from 'app/shared/featureFlags/selectors';
import { CGDOLoadingResponse } from 'app/modules/investigations/types/responses';
import {
  getLoadingFromColdStorageStateForCGDO,
  COLD_STORAGE_WAIT_TIME,
  COLD_STORAGE_MAX_RETRIES,
} from 'app/shared/utils/fastArchHelpers';
import { useRef, useMemo, useEffect } from 'react';

type ActionEventResponse =
  | CachedActionEventResponse
  | ShortActionEventResponse
  | CGDOLoadingResponse;

// Typescript guard to know if the data is loading or not
const isLoadingResponse = (
  data: ActionEventResponse,
): data is CGDOLoadingResponse => {
  return 'data_loader' in data;
};

export const useGetActionEventDetail = (id: string) => {
  const hasReadEventsPermission = useSelector(selectHasReadEventsPermission);
  const useNewEndpoint = useSelector(selectDLEnabledForActionEventDetail);
  const dataUpdateCountRef = useRef(0);

  const query = useQuery<ActionEventResponse, Error, ActionEventResponse>({
    queryFn: () => {
      if (useNewEndpoint) {
        return get<CachedActionEventResponse | CGDOLoadingResponse>(
          `/action-events/cached/${id}`,
        );
      }
      return get<ShortActionEventResponse>(`/action-events/${id}`);
    },
    queryKey: ACTION_EVENT_QUERY_KEYS.getActionEventDetail(id, useNewEndpoint),
    enabled: hasReadEventsPermission && id.length > 0,
    staleTime: 10000,
    refetchInterval: ({ state: { data } }) => {
      if (useNewEndpoint) {
        if (
          data &&
          isLoadingResponse(data) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return COLD_STORAGE_WAIT_TIME;
        }
      }
      // Do not retry by default
      return false;
    },
  });

  const coldStorageLoading = useMemo(
    () =>
      id
        ? getLoadingFromColdStorageStateForCGDO(
            id,
            query.data && isLoadingResponse(query.data)
              ? query.data.data_loader
              : undefined,
          )
        : undefined,
    [id, query.data],
  );

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [id]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  const isLoading = useMemo(() => {
    if (query.isLoading) {
      return true;
    }
    if (query.data && isLoadingResponse(query.data)) {
      return true;
    }
    return false;
  }, [query.isLoading, query.data]);

  const data = useMemo(() => {
    if (!query.data || isLoadingResponse(query.data)) {
      return undefined;
    }
    return query.data;
  }, [query.data]);

  return {
    // This is still accesible if needed for whatever reason
    query,
    // The following values already take into account the cold storage loading state
    data,
    isLoading,
    coldStorageLoading,
  };
};
